import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, ModalContent, ModalFooter, ModalFooterButtons, ModalHeader } from 'ui/molecules/modal';
import Hint from 'ui/atoms/hint';
import Translate from 'ui/atoms/translate';
import Select from 'ui/atoms/select';
import LoadingRing from 'ui/atoms/loading-ring';
import { PortfolioApi, TwoFactorAuthApi, UsersApi } from 'api/apis';
import { AvailableProduct, NetworkTypeEnum, User, Wallet, WalletTypeEnum } from 'api/models';
import useApiCall from 'hooks/use-api-call';
import Section from 'src/ui/atoms/section';
import TfaDeviceSetupModal from 'src/libraries/tfa-device-setup-modal';

export interface AddProductModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const WALLET_TYPE_LABELS: Record<string, string> = {
  [WalletTypeEnum.CASHLINK]: 'Cashlink',
  [WalletTypeEnum.MOBILE]: 'Mobile',
  [WalletTypeEnum.TANGANY]: 'Tangany',
  [WalletTypeEnum.GENERIC]: 'Generic',
};

function walletLabel(w: Wallet, network?: NetworkTypeEnum): string {
  const typeLabel = WALLET_TYPE_LABELS[w.type] || w.type;
  if (network === NetworkTypeEnum.ETHEREUM && w.ethInfo) return `${typeLabel} – ${w.ethInfo.address.slice(0, 10)}…`;
  if (network === NetworkTypeEnum.STELLAR && w.stellarInfo)
    return `${typeLabel} – ${w.stellarInfo.accountId.slice(0, 10)}…`;
  if (w.stellarInfo) return `${typeLabel} – ${w.stellarInfo.accountId.slice(0, 10)}…`;
  if (w.ethInfo) return `${typeLabel} – ${w.ethInfo.address.slice(0, 10)}…`;
  return `${typeLabel} – ${w.id.slice(0, 8)}`;
}

const POLL_INTERVAL = 3000;

const AddProductModal: FunctionComponent<AddProductModalProps> = ({ onClose, onSuccess }) => {
  const { withApi, makeAuthenticatedApi, loading } = useApiCall();

  const api = useMemo(() => makeAuthenticatedApi(PortfolioApi), [makeAuthenticatedApi]);
  const tfaApi = useMemo(() => makeAuthenticatedApi(TwoFactorAuthApi), [makeAuthenticatedApi]);
  const usersApi = useMemo(() => makeAuthenticatedApi(UsersApi), [makeAuthenticatedApi]);

  const [products, setProducts] = useState<AvailableProduct[]>([]);
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedWalletId, setSelectedWalletId] = useState('');

  const [pendingTfa, setPendingTfa] = useState(false);
  const [assignmentId, setAssignmentId] = useState('');
  const [success, setSuccess] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [error, setError] = useState<'generic' | 'tfa_setup' | null>(null);
  const [me, setMe] = useState<User | undefined>();
  const tfaEmailSentRef = useRef(false);
  const pollRef = useRef<ReturnType<typeof setInterval>>(undefined);
  const tfaPollRef = useRef<ReturnType<typeof setInterval>>(undefined);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (tfaPollRef.current) clearInterval(tfaPollRef.current);
    };
  }, []);

  // Load available products on mount
  useEffect(() => {
    withApi(async () => {
      const res = await api.portfolioAvailableProductsList({});
      setProducts(res);
    });
  }, [withApi, api]);

  // Load compatible wallets when product changes
  useEffect(() => {
    if (!selectedProductId) {
      setWallets([]);
      return;
    }
    setSelectedWalletId('');
    withApi(async () => {
      const res = await api.portfolioAvailableProductsWalletsList({ productId: selectedProductId });
      setWallets(res);
    });
  }, [selectedProductId, withApi, api]);

  const startPolling = useCallback(
    (id: string) => {
      pollRef.current = setInterval(async () => {
        try {
          const status = await api.portfolioAssignmentsStatusRetrieve({ assignmentId: id });
          if (status.status === 'SUCCEEDED') {
            if (pollRef.current) clearInterval(pollRef.current);
            setPendingTfa(false);
            setSuccess(true);
          } else if (status.status === 'FAILED') {
            if (pollRef.current) clearInterval(pollRef.current);
            setPendingTfa(false);
            setError('generic');
          }
        } catch (e) {
          console.warn('add-product polling error', e);
        }
      }, POLL_INTERVAL);
    },
    [api],
  );

  const onSubmit = useCallback(async () => {
    setError(null);
    withApi(async () => {
      try {
        const result = await api.portfolioAddProductCreate({
          addProductWalletRequest: { productId: selectedProductId, walletId: selectedWalletId },
        });
        setAssignmentId(result.id);
        if (result.status === 'PENDING') {
          setPendingTfa(true);
          startPolling(result.id);
        } else {
          setSuccess(true);
        }
      } catch (e: any) {
        if (e?.hasErrorCode?.('tfa_setup_required')) {
          setError('tfa_setup');
        } else {
          setError('generic');
        }
      }
    });
  }, [withApi, api, selectedProductId, selectedWalletId, startPolling]);

  // SafeKeeping App setup sub-flow: send init email, poll users/me until
  // tfaDevice.confirmed, then auto-retry the original add-product POST.
  const sendTfaInitEmail = useCallback(() => {
    withApi(async () => {
      await tfaApi.twoFactorAuthDeviceInitializationCreate({
        tFADeviceInitializationRequest: { walletNeeded: false },
      });
    });
  }, [withApi, tfaApi]);

  useEffect(() => {
    if (error !== 'tfa_setup') {
      tfaEmailSentRef.current = false;
      if (tfaPollRef.current) {
        clearInterval(tfaPollRef.current);
        tfaPollRef.current = undefined;
      }
      return;
    }
    if (tfaEmailSentRef.current) return;
    tfaEmailSentRef.current = true;
    sendTfaInitEmail();

    const loadMe = async () => {
      try {
        const u = await usersApi.usersMeRetrieve();
        setMe(u);
      } catch (e) {
        console.warn('users/me poll error', e);
      }
    };
    loadMe();
    tfaPollRef.current = setInterval(loadMe, 2000);
  }, [error, sendTfaInitEmail, usersApi]);

  useEffect(() => {
    if (error === 'tfa_setup' && me?.tfaDevice?.confirmed) {
      if (tfaPollRef.current) {
        clearInterval(tfaPollRef.current);
        tfaPollRef.current = undefined;
      }
      onSubmit();
    }
  }, [error, me?.tfaDevice?.confirmed, onSubmit]);

  const onCancel = useCallback(async () => {
    if (!assignmentId) {
      onClose();
      return;
    }
    if (pollRef.current) clearInterval(pollRef.current);
    try {
      await api.portfolioAssignmentsCancelCreate({
        assignmentId,
      });
    } catch {
      // best-effort cancel
    }
    onClose();
  }, [api, assignmentId, onClose]);

  const handleClose = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (success || pendingTfa) onSuccess();
    onClose();
  }, [onClose, onSuccess, success, pendingTfa]);

  const selectedProduct = products.find((p) => p.id === selectedProductId);
  const productNetwork = selectedProduct?.networkType as NetworkTypeEnum;
  const productOptions = products.map((p) => ({ value: p.id, label: `${p.name} (${p.networkType})` }));
  const walletOptions = wallets.map((w) => ({ value: w.id, label: walletLabel(w, productNetwork) }));

  if (error === 'tfa_setup' && !me?.tfaDevice?.confirmed && !confirmingCancel) {
    return <TfaDeviceSetupModal user={me} onResendEmail={sendTfaInitEmail} onClose={() => setConfirmingCancel(true)} />;
  }

  return (
    <Modal onClose={handleClose}>
      <ModalHeader>
        <Translate name="addProductModal.title" />
      </ModalHeader>
      <ModalContent>
        {success ? (
          <>
            <Translate name="addProductModal.successHeading" as="p" />
            <Hint variant="success">
              <Translate name="addProductModal.success" />
            </Hint>
          </>
        ) : confirmingCancel ? (
          <Hint variant="warning">
            <Translate name="addProductModal.cancelConfirm" />
          </Hint>
        ) : pendingTfa ? (
          <>
            <Translate name="addProductModal.pendingTfaHeading" as="p" />
            <Translate name="addProductModal.pendingTfa" as="p" />
          </>
        ) : (
          <>
            {error === 'tfa_setup' && (
              <Hint variant="warning">
                <Translate name="addProductModal.tfaSetupRequired" />
              </Hint>
            )}
            {error === 'generic' && (
              <Hint variant="danger">
                <Translate name="addProductModal.error" />
              </Hint>
            )}
            {loading && !products.length ? (
              <LoadingRing />
            ) : products.length === 0 ? (
              <Hint variant="info">
                <Translate name="addProductModal.noProducts" />
              </Hint>
            ) : (
              <>
                <Section spacing="small">
                  <Select
                    label={<Translate name="addProductModal.selectProduct" />}
                    options={productOptions}
                    value={selectedProductId}
                    onChange={(val: string) => setSelectedProductId(val)}
                  />
                </Section>
                {selectedProductId && (
                  <>
                    {loading && !wallets.length ? (
                      <LoadingRing />
                    ) : wallets.length === 0 && !loading ? (
                      <Hint variant="info">
                        <Translate name="addProductModal.noWalletsAddresses" />
                      </Hint>
                    ) : (
                      <>
                        <Select
                          label={<Translate name="addProductModal.walletAddress" />}
                          options={walletOptions}
                          value={selectedWalletId}
                          onChange={(val: string) => setSelectedWalletId(val)}
                        />
                        <Hint variant="info">
                          <Translate name="addProductModal.walletAddressHint" />
                        </Hint>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </ModalContent>
      <ModalFooter>
        <ModalFooterButtons
          actionButtons={
            success
              ? [
                  {
                    name: 'close',
                    content: <Translate name="common.close" />,
                    size: 'large',
                    onClick: handleClose,
                  },
                ]
              : confirmingCancel
              ? [
                  {
                    name: 'back',
                    content: <Translate name="common.back" />,
                    size: 'large',
                    onClick: () => setConfirmingCancel(false),
                  },
                  {
                    name: 'cancel',
                    content: <Translate name="common.cancel" />,
                    variant: 'primary',
                    size: 'large',
                    onClick: onCancel,
                  },
                ]
              : pendingTfa
              ? [
                  {
                    name: 'cancel',
                    content: <Translate name="common.cancel" />,
                    size: 'large',
                    onClick: onCancel,
                  },
                ]
              : error === 'tfa_setup'
              ? [
                  {
                    name: 'cancel',
                    content: <Translate name="common.cancel" />,
                    size: 'large',
                    onClick: () => setConfirmingCancel(true),
                  },
                ]
              : [
                  {
                    name: 'cancel',
                    content: <Translate name="common.cancel" />,
                    size: 'large',
                    onClick: onClose,
                  },
                  {
                    name: 'submit',
                    content: <Translate name="common.add" />,
                    variant: 'primary',
                    size: 'large',
                    disabled: !selectedProductId || !selectedWalletId || loading,
                    onClick: onSubmit,
                  },
                ]
          }
        />
      </ModalFooter>
    </Modal>
  );
};

export default AddProductModal;
